import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

export interface LbaActionMenuItem {
  id: string;
  text: string;
  icon?: string;
  disabled?: boolean;
}

@Component({
    selector: 'lba-action-menu',
    imports: [CommonModule],
    templateUrl: './lba-action-menu.component.html',
    styleUrls: ['./lba-action-menu.component.scss']
})
export class LbaActionMenuComponent implements OnDestroy {
  @Input() items: LbaActionMenuItem[] = [];
  @Input() panelWidth: string | number = 280;
  @Input() disabled = false;

  @Output() actionSelect = new EventEmitter<LbaActionMenuItem>();
  @Output() openedChange = new EventEmitter<boolean>();

  @ViewChild('trigger', { read: ElementRef })
  private triggerRef?: ElementRef<HTMLElement>;

  isOpen = false;
  panelTop = 0;
  panelLeft = 0;

  /** Ignores the same click that opened the menu when listening on document. */
  private ignoreDocumentClick = false;
  private readonly onScroll = (): void => {
    if (this.isOpen) {
      this.close();
    }
  };

  @HostBinding('class.is-open')
  get openClass(): boolean {
    return this.isOpen;
  }

  constructor(private host: ElementRef<HTMLElement>) { }

  get panelMinWidth(): number {
    return typeof this.panelWidth === 'number'
      ? this.panelWidth
      : parseInt(String(this.panelWidth), 10) || 280;
  }

  trackByItemId(_: number, item: LbaActionMenuItem): string {
    return item.id;
  }

  toggle(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.close();
      return;
    }
    this.open();
  }

  open(): void {
    if (this.disabled || this.isOpen) {
      return;
    }
    this.updatePanelPosition();
    this.isOpen = true;
    this.ignoreDocumentClick = true;
    this.openedChange.emit(true);
    document.addEventListener('scroll', this.onScroll, true);
    // Allow the opening click to finish before outside-click closes the menu.
    setTimeout(() => {
      this.ignoreDocumentClick = false;
    });
  }

  close(): void {
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.ignoreDocumentClick = false;
    document.removeEventListener('scroll', this.onScroll, true);
    this.openedChange.emit(false);
  }

  onItemClick(item: LbaActionMenuItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (item.disabled) {
      return;
    }
    this.actionSelect.emit(item);
    this.close();
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onScroll, true);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.isOpen) {
      this.updatePanelPosition();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen || this.ignoreDocumentClick) {
      return;
    }
    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) {
      return;
    }
    this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  private updatePanelPosition(): void {
    const trigger =
      this.triggerRef?.nativeElement ??
      this.host.nativeElement.querySelector<HTMLElement>('.lba-action-menu__trigger');
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const viewportPadding = 8;
    const estimatedHeight = Math.max(this.items.length, 1) * 40 + 8;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const openUpward = spaceBelow < estimatedHeight && rect.top > spaceBelow;

    this.panelTop = openUpward
      ? Math.max(viewportPadding, rect.top - gap - estimatedHeight)
      : rect.bottom + gap;

    const maxLeft = Math.max(
      viewportPadding,
      window.innerWidth - this.panelMinWidth - viewportPadding
    );
    this.panelLeft = Math.min(Math.max(viewportPadding, rect.left), maxLeft);
  }
}
